import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Church, MapPin, LogOut, ArrowRight, Clock } from "lucide-react";
import { api } from "../lib/api";
import { usePastorAuth } from "../lib/pastorAuth";
import { ArchWindow } from "../components/ArchWindow";
import { GoogleButton } from "../components/GoogleButton";

const STATUS_STEPS = ["new", "in_review", "accepted", "completed"];
const STATUS_LABEL = { new: "Received", in_review: "Luke is reviewing", accepted: "Accepted", completed: "Completed", declined: "Not a fit right now" };
const STATUS_COLORS = {
  new: "bg-[#E84C4C]/10 text-[#E84C4C] border-[#E84C4C]/30",
  in_review: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  accepted: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  completed: "bg-sky-500/10 text-sky-600 border-sky-500/30",
  declined: "bg-neutral-500/10 text-neutral-500 border-neutral-400/40",
};

const Progress = ({ status }) => {
  const idx = STATUS_STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-2 mt-5" data-testid="application-progress">
      {STATUS_STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2 flex-1">
          <div className={`h-1.5 flex-1 rounded-full transition-colors ${status === "declined" ? "bg-neutral-200" : i <= idx ? "bg-[#E84C4C]" : "bg-[#120716]/10"}`} />
        </div>
      ))}
    </div>
  );
};

const ApplicationCard = ({ a }) => (
  <div className="rounded-2xl bg-white border border-[#120716]/10 shadow-sm p-6 md:p-8" data-testid={`my-application-${a.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h3 className="font-orbitron font-bold text-lg text-[#120716] tracking-wide">{a.church_name}</h3>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[#120716]/55"><MapPin className="w-3.5 h-3.5 text-[#E84C4C]" /> {a.location}</p>
      </div>
      <span data-testid="my-application-status" className={`rounded-full border px-3 py-1 text-[10px] font-orbitron tracking-widest uppercase ${STATUS_COLORS[a.status] || STATUS_COLORS.new}`}>
        {STATUS_LABEL[a.status] || a.status}
      </span>
    </div>
    <Progress status={a.status} />
    {a.needs?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mt-5">
        {a.needs.map((n) => <span key={n} className="rounded-full bg-[#FFF5F2] border border-[#E84C4C]/20 text-[#C13B3B] text-[10px] px-2.5 py-1">{n}</span>)}
      </div>
    )}
    <p className="mt-4 text-sm text-[#120716]/65 leading-relaxed">{a.vision}</p>
    <p className="mt-4 flex items-center gap-1.5 text-[11px] text-[#120716]/45"><Clock className="w-3.5 h-3.5" /> Submitted {new Date(a.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
  </div>
);

const SignInGate = ({ login }) => (
  <div className="min-h-screen bg-[#FFF5F2] flex items-center justify-center px-6">
    <div className="glass rounded-2xl p-10 w-full max-w-md text-center" data-testid="account-signin-card">
      <div className="flex justify-center"><ArchWindow size={34} /></div>
      <h1 className="font-orbitron font-bold text-xl text-[#120716] mt-6 tracking-widest">MY APPLICATIONS</h1>
      <p className="mt-3 text-sm text-[#120716]/60 leading-relaxed">Sign in with the Google account you used to apply, and I'll show you exactly where your church's application stands.</p>
      <GoogleButton onClick={login} className="mt-8 w-full" testId="account-google-signin" />
      <Link to="/" className="block mt-6 text-xs text-[#120716]/50 hover:text-[#E84C4C]">← Back to site</Link>
    </div>
  </div>
);

export default function Account() {
  const { pastor, loading, login, logout } = usePastorAuth();
  const [apps, setApps] = useState(null);

  useEffect(() => {
    if (!pastor) return;
    api.get("/my/applications").then((r) => setApps(r.data)).catch(() => setApps([]));
  }, [pastor]);

  if (loading) return <div className="min-h-screen bg-[#FFF5F2] flex items-center justify-center text-[#E84C4C] font-orbitron tracking-widest text-xs">LOADING...</div>;
  if (!pastor) return <SignInGate login={login} />;

  return (
    <div className="min-h-screen bg-[#FFF5F2]" data-testid="account-page">
      <header className="bg-white border-b border-[#120716]/10">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="account-logo">
            <ArchWindow size={24} />
            <div className="leading-none">
              <span className="font-orbitron font-black text-[#120716] text-base tracking-widest">DIGITAL</span>
              <span className="block font-orbitron text-[9px] tracking-[0.5em] text-[#E84C4C]">MISSIONS</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3" data-testid="account-user">
              {pastor.picture ? <img src={pastor.picture} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full border border-[#120716]/10" /> : <div className="w-9 h-9 rounded-full bg-[#E84C4C]/15 text-[#E84C4C] flex items-center justify-center font-bold">{(pastor.name || pastor.email)[0]?.toUpperCase()}</div>}
              <div className="leading-tight">
                <div className="text-sm font-semibold text-[#120716]">{pastor.name || "Signed in"}</div>
                <div className="text-[11px] text-[#120716]/50">{pastor.email}</div>
              </div>
            </div>
            <button onClick={logout} data-testid="account-logout" className="inline-flex items-center gap-2 text-xs font-semibold text-[#120716]/60 hover:text-[#E84C4C] transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-orbitron text-xs tracking-[0.4em] text-[#E84C4C]">MY APPLICATIONS</p>
            <h1 className="font-orbitron font-bold text-2xl sm:text-3xl text-[#120716] mt-3">Welcome, {pastor.name?.split(" ")[0] || "friend"}.</h1>
            <p className="mt-3 text-sm text-[#120716]/60 max-w-xl leading-relaxed">Every application you send lands on my desk, and this is where you'll see it move: received, reviewed, accepted, built.</p>
          </div>
          <Link to="/#apply" data-testid="account-new-application" className="inline-flex items-center gap-2 rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white text-sm font-semibold px-6 py-3 transition-all">
            <Church className="w-4 h-4" /> Apply for another church <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10 space-y-6">
          {apps === null ? (
            <p className="text-sm text-[#120716]/45 italic">Loading your applications...</p>
          ) : apps.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center" data-testid="no-my-applications">
              <div className="flex justify-center"><ArchWindow size={30} /></div>
              <h2 className="font-orbitron font-bold text-lg text-[#120716] mt-5">NOTHING HERE YET</h2>
              <p className="mt-3 text-sm text-[#120716]/60 max-w-md mx-auto leading-relaxed">You're signed in, but you haven't told me about your church yet. It takes about three minutes, and it never costs your church a thing.</p>
              <Link to="/#apply" data-testid="account-start-application" className="inline-block mt-6 rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white text-sm font-semibold px-7 py-3 transition-all">Tell Me About Your Church</Link>
            </div>
          ) : (
            apps.map((a) => <ApplicationCard key={a.id} a={a} />)
          )}
        </div>
        <p className="mt-12 text-center text-xs italic text-[#C13B3B]">"Commit your work to the Lord, and your plans will be established." — Proverbs 16:3</p>
      </main>
    </div>
  );
}
